import * as React from "react";
import { app } from "@microsoft/teams-js";
import { RouteComponentProps } from 'react-router-dom';
import {
    Box,
    Flex,
    Loader,
    List,
    Text,
    Label,
    Segment,
    Button,
    Tooltip,
    Provider,
    teamsTheme,
    Input,
} from "@fluentui/react-northstar";
import { InfoIcon, SearchIcon, TeamsMonochromeIcon } from '@fluentui/react-icons-northstar';
import { initializeIcons, Icon } from '@fluentui/react';
import { getMeetingsData, getAppConfig, getAppSettings, getMeetingQueueData } from '../../api/publicapp-api';
import { withTranslation, WithTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { debounce } from 'lodash';
import { updateLocale } from "../../i18n";
import statusQueueConstants from "../../constants/status-queue";
import "./meetings.scss";

interface IState {
    theme: string,
    embed: boolean,
    loading: boolean,
    loadingTeams: boolean,
    meetingsData: any,
    meetingsDataOriginal: any,
    meetingQueueData: any,
    selectedMeeting: any,
    appConfigs: any,
    appSettings: any,
    tipoCategoia: any, 
    teamsApp: boolean,
    buttonClicked: boolean,
    loadingMeeting: boolean,
    welcomeText: string,
    searchTerm: string,
}

export interface IAppSettings {
    token: string | null,
    telemetry: string | null,
    theme: string | null
}

export type MeetingsDataPageProps = {} & RouteComponentProps & WithTranslation;

class MeetingsDataPage extends React.Component<MeetingsDataPageProps, IState> {
    token?: string | null = null;
    telemetry?: any = null;
    theme?: string | null;
    locale?: string | null;
    userObjectId?: string = "";
    tenantId?: string = "";
    localize: TFunction;
    teamsContext: boolean;

    constructor(props: MeetingsDataPageProps) {
        super(props);

        this.localize = this.props.t;
        initializeIcons();
        let search = window.location.search;
        let params = new URLSearchParams(search);
        this.theme = params.get("theme");
        this.locale = params.get("locale");

        const hasAncestorOrigins = 'ancestorOrigins' in window.location;

        if (hasAncestorOrigins) {
            this.teamsContext = window.location.ancestorOrigins[0] &&
                window.location.ancestorOrigins[0].toLowerCase().includes("teams");
        } else {
            this.teamsContext = false;
        }

        this.state = {
            loading: true,
            loadingTeams: true,
            theme: this.theme ? this.theme : "default",
            embed: params.has("embed") ?? false,
            meetingsData: null,
            meetingsDataOriginal: null,
            meetingQueueData: null,
            selectedMeeting: null,
            appConfigs: null,
            appSettings: null,
            tipoCategoia: null,
            teamsApp: false,
            buttonClicked: false,
            loadingMeeting: true,
            welcomeText: null,
            searchTerm: "",
        }
    }


    /**
    * Used to initialize this component.
    */
    public async componentDidMount() {
        if (this.teamsContext) {
            this.setState({ teamsApp: true });

            app.initialize();
            app.getContext().then((context: app.Context) => {

            });

            if (window.localStorage.getItem("queueData")) {
                const queueDataJSON = window.atob(window.localStorage.getItem("queueData"));
                const queueData = JSON.parse(queueDataJSON);

                this.existsQueueInProgress(queueData["ancora"], queueData["queueId"]);
                this.init();
            } else {
                this.setState({
                    loadingTeams: false,
                    loadingMeeting: false,
                });

                this.init();
            }
            
        } else {
            this.setState({
                loadingTeams: false,
                loadingMeeting: false,
            });

            this.init();
        }
    }

    /**
    * Init calls.
    */
    public init = async () => {
        await this.getAppConfig();
        await this.getAppSettings();

        if (!this.state.embed)
            this.getMeetingsDataResponse();
        else
            this.setState({ loading: false, meetingsData: [] });
    }


    /**
    * Validates if exists a queue in prgress for user.
    */
    public existsQueueInProgress = async (ancora: string, queueId: string) => {
        const response = await getMeetingQueueData(ancora, queueId);

        if (response.status === 200 && response.data) {
            this.setState({
                meetingQueueData: response.data,
            });

            if (response.data.status === statusQueueConstants.naFila ||
                response.data.status === statusQueueConstants.atendimentoIniciado ||
                response.data.status === statusQueueConstants.emAtendimento) {
                window.location.href = `/meeting-queue/${ancora}?data=${window.localStorage.getItem('queueData')}`;
            } else {
                this.setState({
                    loadingMeeting: false,
                    loadingTeams: false,
                });
            }
        }

        this.setState({
            loadingTeams: false,
        })
    }

    /**
     * Get Meetings Data
     * */
    private async getMeetingsDataResponse() {
        const response = await getMeetingsData();       

        if (response.status === 200 && response.data) {
            this.setState({
                meetingsData: response.data,
                meetingsDataOriginal: response.data,
            });
        }
        
        this.setState({ loading: false });
    }

    /**
     * Get App Configs
     * */
    private async getAppConfig() {
        const response = await getAppConfig();

        if (response.status === 200 && response.data) {            
            this.setState({
                appConfigs: response.data,
            });

            document.title = this.state.appConfigs.pageTitle;
        }
    };

    /**
     * Get App Settings
     * */
    private async getAppSettings() {
        const response = await getAppSettings();

        if (response.status === 200 && response.data) {
            this.setState({
                appSettings: response.data,
                welcomeText: response.data.welcomeText,
            });

            if (response.data.language)
                updateLocale(response.data.language);
        }
    };

    /**
     * Normalize string
     * */
    private normalizeText = (text: string) => {
        return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };

    /**
     * Filter units
     * */
    private debouncedSearch = debounce((value: string) => {
        const normalizedSearch = this.normalizeText(value.trim());

        const searchTerms = normalizedSearch.split(/\s+/);

        const filtered = this.state.meetingsDataOriginal.filter((m: any) => {
            const normalizedName = this.normalizeText(m.name || "");
            return searchTerms.every(term => normalizedName.includes(term));
        });

        this.setState({ meetingsData: filtered });
    }, 500);

    /**
     * Parse week days with locale
     * */
    public daysOfWeekLocale = (day: string) => {
        return day
            .replace(/segunda-feira/, this.localize('SegundaFeira'))
            .replace(/terça-feira/, this.localize('TercaFeira'))
            .replace(/quarta-feira/, this.localize('QuartaFeira'))
            .replace(/quinta-feira/, this.localize('QuintaFeira'))
            .replace(/sexta-feira/, this.localize('SextaFeira'))
            .replace(/sabado/, this.localize('Sabado'))
            .replace(/domingo/, this.localize('Domingo'))
    }

    /**
     * Render the meeting data.
     * */
    private renderMeetingsData() {
        return this.state.meetingsData.map((meeting: any) => {
            var horarios = '';

            if (meeting.horarioAtendimentoDias) {
                horarios += `${this.localize("HorariosAtendimento")}: ` + this.daysOfWeekLocale(meeting.horarioAtendimentoDias);
            }

            if (meeting.horarioAtendimentoInicial) {
                horarios += ` ${this.localize("Das")} ` + meeting.horarioAtendimentoInicial;
            }

            if (meeting.horarioAtendimentoFinal) {
                horarios += ` ${this.localize("Ate")} ` + meeting.horarioAtendimentoFinal + ` (${this.localize("HorarioBrasilia")})`;
            }

            return {
                'key': meeting.Ancora,
                'header': (
                    <Flex gap="gap.medium" vAlign="center">
                        <Flex>
                            {meeting.name}
                        </Flex>

                        {horarios != "" &&
                            <Tooltip trigger={<Icon className="clock-icon" iconName="Clock" />} content={horarios} position="after" align="center" />
                        }
                    </Flex>
                 ),
                'content': '',
                'endMedia': (
                    <Flex gap="gap.large">
                        <Button
                            icon={<TeamsMonochromeIcon />}
                            content={this.localize("Entrar")}
                            onClick={() => {
                                if (!this.state.buttonClicked) {
                                    this.setState({ buttonClicked: true });

                                    setTimeout(() => this.setState({ buttonClicked: false }), 2000);

                                    if (this.state.teamsApp)
                                        window.location.href = `/meeting/${meeting.Ancora}?internal=true`;
                                    else
                                        window.open(`/meeting/${meeting.Ancora}`);
                                }
                            }}
                            primary
                        />
                    </Flex>
                )
            }
        });
    }

    /**
     * Render this component.
     * */
    public render() {
        if (this.state.loading || this.state.loadingTeams) {
            return (
                <Flex gap="gap.small" hAlign="center" vAlign="center" styles={{ margin: "20% 0" }}>
                    <Flex.Item align="end">
                        <Loader label={this.localize("Carregando")} />
                    </Flex.Item>
                </Flex>
            );
        }
        else if (this.state.meetingsData && !this.state.loadingMeeting) {
            return (
                <Provider theme={teamsTheme} styles={{ background: 'transparent' }}>
                    {this.state.appConfigs &&
                        <div hidden={this.state.embed} className="header">
                            <Segment
                                className="headerLine"
                                inverted
                            >
                                <Flex>
                                    <Flex.Item>
                                        <Flex className="logo" gap="gap.small" vAlign="center">
                                            <img className="logoIcon" src="https://studio365cdn.azureedge.net/balcaovirtual/logov2.svg" />
                                            <Text weight="semibold" content={this.localize("BalcaoVirtual")} size="medium" />
                                        </Flex>
                                    </Flex.Item>
                                    <Flex.Item grow>
                                        <Flex hAlign="center" vAlign="center">
                                            <Label className="headerLabel" content={this.state.appConfigs.tribunal} />
                                        </Flex>
                                    </Flex.Item>
                                    <Flex.Item>
                                        <Flex gap="gap.smaller" vAlign="center" className="sobre">
                                            <InfoIcon outline />
                                            <Text weight="light" content={this.localize("Sobre")} size="small" />
                                        </Flex>
                                    </Flex.Item>
                                </Flex>
                            </Segment>
                        </div>
                    }
               
                    <Flex className="meetings-wrapper" gap="gap.small" hAlign="center" vAlign="center" column padding="padding.medium">
                        <Flex className="welcome-card" gap="gap.small" hAlign="start" vAlign="start">
                            <img className="logoIcon" src="https://studio365cdn.azureedge.net/balcaovirtual/logov2.svg" />

                            <Flex className="welcome-message">
                                <div>
                                    {this.state.welcomeText ?
                                        <div dangerouslySetInnerHTML={{ __html: this.state.welcomeText }} />
                                        :
                                        <>
                                            <span className="welcome-header">Olá, Seja bem vindo ao <strong>Balcão Virtual</strong>.</span>
                                            <span>Ingresse em uma das unidades abaixo para entrar na fila do atendimento, clicando em <strong>entrar</strong>.</span>
                                            <span>Ao ser atendido, você poderá optar pelo atendimento via <strong>chat</strong>, ou ingressar na <strong>reunião</strong>.</span>
                                        </>
                                    }
                                </div>

                                <img className="welcomeImg" src="https://studio365cdn.azureedge.net/balcaovirtual/welcome.svg" />
                            </Flex>
                        </Flex>

                        <Flex className="search-container" gap="gap.medium" vAlign="center">
                            <Input
                                icon={<SearchIcon />}
                                placeholder="Buscar unidade"
                                iconPosition="start"
                                onChange={(e, data) => {
                                    const value = data?.value?.toString() || "";
                                    this.setState({ searchTerm: value });
                                    this.debouncedSearch(value);
                                }}
                            />
                        </Flex>

                        <Flex.Item align="center">
                            <Box className={this.state.embed ? "box-meetings fill" : "box-meetings"}>
                                {!this.state.embed &&
                                    (this.state.meetingsData?.length > 0 ?
                                        <List key="items-meetings" items={this.renderMeetingsData()} />
                                            :
                                        <Text styles={{ margin: "10px" }} align="center" content={this.localize("NenhumaUnidade")} />)
                                }

                                {this.state.embed &&
                                    (this.state.meetingsData?.length > 0 ?
                                        <List key="items-meetings" items={this.renderMeetingsData()} />
                                        :
                                        <React.Fragment />)
                                }
                                
                            </Box>
                        </Flex.Item>
                    </Flex>
                </Provider>
            );
        }

        return (
            <div></div>
        );
    }
}

const meetingsDataPageWithTranslation = withTranslation()(MeetingsDataPage);
export default meetingsDataPageWithTranslation;